/**
 * ReceiveScreen — Show wallet address + QR code for receiving payments.
 *
 * Features: large QR code, tap-to-copy address, Web Share API.
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { useWallet } from '../WalletContext.js';
import { shortenAddress, getChainConfig } from '@tokenstreaming/shared';

export function ReceiveScreen({ onBack }: { onBack: () => void }): ReactNode {
  const { status, chainId } = useWallet();
  const chain = getChainConfig(chainId);
  const address = status.state === 'unlocked' ? status.address : '';
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!address) return;
    void QRCode.toDataURL(address, {
      width: 280,
      margin: 3,
      color: { dark: '#000000', light: '#ffffff' },
      errorCorrectionLevel: 'H',
    }).then(setQrDataUrl);
  }, [address]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CryptoCall Wallet Address',
          text: `My ${chain.name} address: ${address}`,
        });
      } catch {
        // User cancelled or share failed
      }
    } else {
      await handleCopy();
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Receive</h2>
      </div>
      <div className="screen-body centered">
        <p className="text-secondary">
          Share your address to receive {chain.nativeSymbol} or tokens on {chain.name}
        </p>

        {/* QR Code */}
        <div className="qr-container">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Wallet address QR code" className="qr-image" />
          ) : (
            <div className="qr-placeholder">Generating QR...</div>
          )}
        </div>

        {/* Address display */}
        <div className="address-display" onClick={() => void handleCopy()}>
          <span className="address-full">{address}</span>
          <span className="copy-label">{copied ? 'Copied!' : 'Tap to copy'}</span>
        </div>

        {/* Actions */}
        <div className="receive-actions">
          <button className="btn-secondary btn-full" onClick={() => void handleCopy()}>
            {copied ? 'Copied!' : 'Copy Address'}
          </button>
          {'share' in navigator && (
            <button className="btn-ghost btn-full" onClick={() => void handleShare()}>
              Share
            </button>
          )}
        </div>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
